import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Card } from "./ui/card";
import { Badge } from "./ui/badge";
import { Flame, Eye, ArrowRight, Sparkles } from "lucide-react";

const fmtBRL = (v) =>
  (Number(v) || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const fmtData = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
};

export default function ParaHojeWidget() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/ia-estrategica/para-hoje")
      .then(({ data }) => setItems(data.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <Card className="p-5 bg-gradient-to-br from-orange-50 to-red-50 border-orange-200">
        <div className="text-sm text-muted-foreground">Carregando inteligência comercial...</div>
      </Card>
    );
  }

  if (items.length === 0) {
    return (
      <Card className="p-5 bg-gradient-to-br from-slate-50 to-slate-100 border-slate-200" data-testid="para-hoje-vazio">
        <div className="flex items-center gap-2 mb-2">
          <Flame size={18} className="text-orange-600" />
          <h3 className="font-display font-bold" style={{ color: "#1B3A6B" }}>🔥 Para hoje</h3>
        </div>
        <p className="text-sm text-muted-foreground">
          Nenhuma proposta em alta temperatura nos últimos 7 dias.
          <br /><span className="text-xs">Quando um cliente abrir sua proposta 2x ou mais sem assinar, vai aparecer aqui.</span>
        </p>
      </Card>
    );
  }

  return (
    <Card
      className="p-5 border-l-4"
      style={{
        background: "linear-gradient(135deg, #FFF7ED 0%, #FEF2F2 100%)",
        borderLeftColor: "#F97316",
      }}
      data-testid="para-hoje-widget"
    >
      <div className="flex items-center gap-2 mb-3">
        <div className="relative">
          <Flame size={20} className="text-orange-600 animate-pulse" />
        </div>
        <h3 className="font-display font-bold text-lg" style={{ color: "#1B3A6B" }}>
          🔥 Para hoje
        </h3>
        <Badge className="text-white border-0" style={{ background: "#F97316" }}>
          {items.length}
        </Badge>
        <span className="text-xs text-muted-foreground ml-auto">
          Propostas em alta temperatura
        </span>
      </div>

      <div className="space-y-2">
        {items.map((p) => {
          const opens = p.opens_count || 0;
          return (
            <Link
              key={p.id}
              to={`/app/leads/${p.lead_id}`}
              className="block bg-white rounded-lg p-3 border border-orange-200 hover:border-orange-400 hover:shadow-md transition-all group"
              data-testid={`para-hoje-${p.id}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                    <span className="font-display font-bold text-sm" style={{ color: "#1B3A6B" }}>
                      {p.cliente_nome}
                    </span>
                    <Badge className="text-xs bg-orange-100 text-orange-800 border-orange-300">
                      <Eye size={10} className="mr-1" /> {opens}× aberto
                    </Badge>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Proposta {p.numero} · {fmtBRL(p.valor_total_geral)} · Última abertura: {fmtData(p.last_opened_at)}
                  </div>
                </div>
                <div className="flex items-center gap-1 text-orange-600 group-hover:translate-x-1 transition-transform">
                  <span className="text-xs font-bold">Abrir</span>
                  <ArrowRight size={14} />
                </div>
              </div>
            </Link>
          );
        })}
      </div>

      <div className="text-xs text-muted-foreground mt-3 pt-3 border-t border-orange-200 flex items-center gap-1">
        <Sparkles size={11} className="text-purple-600" />
        Sugestão: use a IA Estratégica em cada negócio para gerar o script perfeito antes de ligar.
      </div>
    </Card>
  );
}
